import type p5 from 'p5'
import type { BaseMarker, Point, Segment } from './types'
import { RectangleMarker } from './markers/rectangle/rectangleMarker'
import { PolygonMarker } from './markers/polygon/polygonMarker'
import { RectangleMarkerStateManager } from './markers/rectangle/rectangleMarkerStateManager'
import { PolygonMarkerStateManager } from './markers/polygon/polygonMarkerStateManager'

/**
 * Calculates the cross product of three given points to return the
 * orientation. The orientation allows us to determine the relative
 * position of the three points.
 * @param {Point} a Two dimensional point
 * @param {Point} b Two dimensional point
 * @param {Point} c Two dimensional point
 * @returns {number} 0-2
 * 0: Three points are collinear
 * 1: Sequence of points (a,b,c) make a clockwise turn
 * 2: Sequence of points (a,b,c) make a counterclockwise turn
 */
function orientation(a: Point, b: Point, c: Point): number {
  //Equation for cross product
  const val = (b.y - a.y) * (c.x - b.x) - (b.x - a.x) * (c.y - b.y)
  // collinear
  if (val === 0) return 0
  // clock or counterclockwise
  return val > 0 ? 1 : 2
}

/**
 * Determines if two line segments intersect given
 * @param {segment} segment1 Two dimensional line segment
 * @param {segment} segment2 Two dimensional line segment
 * @returns
 */
function doSegmentsIntersect(segment1: Segment, segment2: Segment) {
  const p1 = segment1.start
  const q1 = segment1.end
  const p2 = segment2.start
  const q2 = segment2.end

  // Check if the segments share any endpoints and ignore intersection in such cases
  if (
    (p1.x === p2.x && p1.y === p2.y) ||
    (p1.x === q2.x && p1.y === q2.y) ||
    (q1.x === p2.x && q1.y === p2.y) ||
    (q1.x === q2.x && q1.y === q2.y)
  ) {
    return false // Segments share an endpoint, so no intersection in the context of a polygon
  }

  // Find orientations
  const o1 = orientation(p1, q1, p2)
  const o2 = orientation(p1, q1, q2)
  const o3 = orientation(p2, q2, p1)
  const o4 = orientation(p2, q2, q1)

  // General case
  if (o1 !== o2 && o3 !== o4) return true

  return false // Doesn't intersect
}

/**
 * Calculates the midpoint of a line segment defined by two points.
 * @param {number} x1 - The x-coordinate of the first endpoint.
 * @param {number} y1 - The y-coordinate of the first endpoint.
 * @param {number} x2 - The x-coordinate of the second endpoint.
 * @param {number} y2 - The y-coordinate of the second endpoint.
 * @returns {Point} An object containing the x and y coordinates of the midpoint.
 */
function calculateMidpoint(x1: number, y1: number, x2: number, y2: number): Point {
  const x = (x1 + x2) / 2
  const y = (y1 + y2) / 2
  return { x, y }
}

/**
 * Determine if two 2D points are within the given distance
 * @param {Point} point1 2D Point
 * @param {Point} point2 2D Point
 * @param {number} distance Distance to compare the distance between two points
 * @returns {boolean} Returns true  if point are at given distance or closer; false otherwise.
 */
function arePointsWithinDistance(point1: Point, point2: Point, distance: number) {
  const dx = point2.x - point1.x
  const dy = point2.y - point1.y
  const distanceBetweenPoints = Math.sqrt(dx * dx + dy * dy)

  return distanceBetweenPoints <= distance
}

/**
 * Check if given point if located to the left of a line segment when looking
 * from the start to the end of the segment
 * @param {Point} point 2D Point
 * @param {Segment} segment 2D Line Segment
 * @returns {boolean} True if the provided point is within the provded line segment; false otherwise.
 */
function withinSegment(point: Point, segment: Segment): boolean {
  const a = segment.start
  const b = segment.end
  const p = point

  // Check if point is at same height as the segment
  if (p.y < Math.min(a.y, b.y) || p.y > Math.max(a.y, b.y)) {
    return false
  }

  // Calculate the x-coordinate of the point where the line through 'a' and 'b' intersects the line through 'p' with horizontal direction
  const x = a.x + ((p.y - a.y) * (b.x - a.x)) / (b.y - a.y)

  return x > p.x
}

/**
 * Checks if three given points are collinear.
 *
 * @param {Point} point1 - The first point, an object with x and y coordinates.
 * @param {Point} point2 - The second point, an object with x and y coordinates.
 * @param {Point} point3 - The third point, an object with x and y coordinates.
 * @returns {boolean} Returns true if the three points are collinear, otherwise false.
 */
function isCollinear(point1: Point, point2: Point, point3: Point) {
  // A simple collinearity check using the area of the triangle formed by the points
  return (
    Math.abs(
      (point1.x * (point2.y - point3.y) + point2.x * (point3.y - point1.y) + point3.x * (point1.y - point2.y)) / 2
    ) < 1e-10
  )
}

/**
 * Determine whether the given point lies within the polygon drawn by the given points
 * @param {Point} point 2D Point
 * @param {Array<Point>} points Array of 2D Point
 * @returns {boolean} Returns true if the point is contained in polygon defined by the array of points
 */
function polygonContainsPoint(point: Point, points: Array<Point>): boolean {
  let count = 0
  points.forEach((pointI, i) => {
    const nextPointIndex = (i + 1) % points.length //Modulo ensures we wrap around to first segment
    const nextPoint = points[nextPointIndex]

    if (withinSegment(point, { start: pointI, end: nextPoint })) {
      count++
    }
  })
  return count % 2 !== 0
}

/**
 * Given a segment, determine if the segment is intersection any of the markers defined segments
 * @param {Segment} newSegment created by 2 2D points
 * @param {Array<Segment>} existingSegments array of 2d line segments
 * @returns {boolean} Returns true if the newSegment intersects of the existing segments, false otherwise.
 */
function doesSegmentIntersectSegments(newSegment: Segment, existingSegments: Array<Segment>) {
  let intersects = false
  existingSegments.some((segment) => {
    if (doSegmentsIntersect(segment, newSegment)) {
      intersects = true
      return true
    }
  })
  return intersects
}

//TODO typing for params and return
function getRectangleSegments(rectangleState: RectangleMarkerStateManager) {
  const x = rectangleState.position.x
  const y = rectangleState.position.y
  const width = rectangleState.dimensions.width
  const height = rectangleState.dimensions.height

  const corners = [
    { x, y },
    { x: x + width, y },
    { x: x + width, y: y + height },
    { x, y: y + height },
  ]

  return [
    { start: corners[0], end: corners[1] },
    { start: corners[1], end: corners[2] },
    { start: corners[2], end: corners[3] },
    { start: corners[3], end: corners[0] },
  ]
}

//TODO typing
function getRectangleVertices(rectangleState: RectangleMarkerStateManager) {
  const x = rectangleState.position.x
  const y = rectangleState.position.y
  const width = rectangleState.dimensions.width
  const height = rectangleState.dimensions.height

  const vertices: Point[] = [
    { x: x, y: y },
    { x: x + width, y: y },
    { x: x + width, y: y + height },
    { x: x, y: y + height },
  ]

  return vertices
}

function getPolygonVertices(polygonState: PolygonMarkerStateManager) {
  const vertices: Point[] = []
  polygonState.vertices.forEach((vertex) => {
    vertices.push({
      x: vertex.x,
      y: vertex.y,
    })
  })

  return vertices
}
function rectanglePolygonEdgeIntersect(rectangleVertices, polygonVertices) {
  // Convert vertices to segments
  const rectangleSegments = rectangleVertices.map((point, i, arr) => ({
    start: point,
    end: arr[(i + 1) % arr.length],
  }))

  const polygonSegments = polygonVertices.map((point, i, arr) => ({
    start: point,
    end: arr[(i + 1) % arr.length],
  }))

  // Check for intersections between rectangle and polygon segments
  return rectangleSegments.some((rectangleSegment) =>
    polygonSegments.some((polygonSegment) => doSegmentsIntersect(rectangleSegment, polygonSegment))
  )
}

function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let intersectCount = 0
  const numPoints = polygon.length

  for (let i = 0; i < numPoints; i++) {
    const vertex1 = polygon[i]
    const vertex2 = polygon[(i + 1) % numPoints]

    // Check if point is on vertex
    if (point.x === vertex1.x && point.y === vertex1.y) {
      return true
    }

    // Check if point is on an edge
    if (
      point.y > Math.min(vertex1.y, vertex2.y) &&
      point.y <= Math.max(vertex1.y, vertex2.y) &&
      point.x <= Math.max(vertex1.x, vertex2.x) &&
      vertex1.y !== vertex2.y
    ) {
      const xinters = ((point.y - vertex1.y) * (vertex2.x - vertex1.x)) / (vertex2.y - vertex1.y) + vertex1.x
      if (xinters === point.x) {
        return true // Point is on the edge
      }
      if (vertex1.x === vertex2.x || point.x <= xinters) {
        intersectCount++
      }
    }
  }

  return intersectCount % 2 !== 0
}

//TODO typings for rectangle def
function polygonPointInsideRectangle(polygonPoints: Point[], rectanglePoints: Point[]): boolean {
  return polygonPoints.some((point) => pointInPolygon(point, rectanglePoints))
}

//TODO typing for params
function doRectanglesIntersect(rect1: Point[], rect2: Point[]): boolean {
  // Extract the x and y coordinates of the vertices for each rectangle
  const [rect1MinX, rect1MaxX] = [Math.min(...rect1.map((p) => p.x)), Math.max(...rect1.map((p) => p.x))]
  const [rect1MinY, rect1MaxY] = [Math.min(...rect1.map((p) => p.y)), Math.max(...rect1.map((p) => p.y))]

  const [rect2MinX, rect2MaxX] = [Math.min(...rect2.map((p) => p.x)), Math.max(...rect2.map((p) => p.x))]
  const [rect2MinY, rect2MaxY] = [Math.min(...rect2.map((p) => p.y)), Math.max(...rect2.map((p) => p.y))]

  // Check for overlap in x and y ranges
  return !(rect1MaxX < rect2MinX || rect2MaxX < rect1MinX || rect1MaxY < rect2MinY || rect2MaxY < rect1MinY)
}

function doesRectangleIntersectPolygon(rectangleVerices: Point[], polygonVertices: Point[]): boolean {
  // Check for edge intersections
  if (rectanglePolygonEdgeIntersect(rectangleVerices, polygonVertices)) {
    return true
  }
  // Check if any point of the polygon is inside the rectangle
  if (polygonPointInsideRectangle(polygonVertices, rectangleVerices)) {
    return true
  }
  // No intersection found
  return false
}

function getIntersectingMarkers(
  selectionMarker: RectangleMarker,
  markers: Array<BaseMarker<any, any, any, any>>
): Array<BaseMarker<any, any, any, any>> {
  const intersectedMarkers: Array<BaseMarker<any, any, any, any>> = []
  const selectionMarkerVertices = getRectangleVertices(selectionMarker.state)
  markers.forEach((marker) => {
    if (marker.typeName === PolygonMarker.typeName) {
      const polygonVertices = getPolygonVertices(marker.state)
      const intersects = doesRectangleIntersectPolygon(selectionMarkerVertices, polygonVertices)
      if (intersects) intersectedMarkers.push(marker)
    }
    if (marker.typeName === RectangleMarker.typeName) {
      const rectangleVertices = getRectangleVertices(marker.state)
      const intersects = doRectanglesIntersect(selectionMarkerVertices, rectangleVertices)
      if (intersects) intersectedMarkers.push(marker)
    }
  })
  return intersectedMarkers
}

/**
 * Converts window mouse x and y positions to coordinates relative to the dom element
 * Note: Elements with padding or margin will not properly compute
 * @param {number} windowMouseX Mouse x position relative to browser window
 * @param {number} windowMouseY Mouse y position relative to browser window
 * @param {HTMLElement} element HTMLElement which the local coordinates are calculated against
 * @returns {Point} 2D Point indicating the position in respect to the element provided.
 */
function clientToLocalCoordinates(windowMouseX: number, windowMouseY: number, element: HTMLElement) {
  const boundingRect = element.getBoundingClientRect()

  const width = element.clientWidth
  const height = element.clientHeight

  const scaleX = Math.round(boundingRect.width) / width
  const scaleY = Math.round(boundingRect.height) / height

  const mouseX = (windowMouseX - boundingRect.left) / scaleX
  const mouseY = (windowMouseY - boundingRect.top) / scaleY

  return {
    x: mouseX,
    y: mouseY,
  }
}

/**
 * Applies transform matrix to the provided x and y values
 * @param {number} x X position
 * @param {number} y Y Position
 * @param {Array<number>} matrix Canvas 2D context trasform Matrix
 * @returns {Point} Returns the point generated after applying the transform matrix to the provided x and y coordindates.
 */
function applyMatrix(x: number, y: number, matrix: Array<number>) {
  const [a, b, c, d, e, f] = matrix

  return {
    x: a * x + c * y + e,
    y: b * x + d * y + f,
  }
}

/**
 *
 * @param {number} x Mouse X Position
 * @param {number} y Mouse Y Position
 * @param {Array<number>} matrix Canvas 2D context trasform Matrix
 * @returns {Point} Returns the point generated after inverse applying the transform matrix to the provided x and y coordindates.
 */
function inverseApplyMatrix(x: number, y: number, matrix: Array<number>) {
  //Assuming matrix = [a, b, c, d, e, f] and corresponds with
  //| a c e |
  //| b d f |
  //| 0 0 1 |
  const [a, b, c, d, e, f] = matrix

  //Calculate determinant for a 2D matrix
  const det = a * d - b * c

  //Calculate the inverse matrix components
  const ia = d / det
  const ib = -b / det
  const ic = -c / det
  const id = a / det
  const ie = (c * f - d * e) / det
  const if_ = (b * e - a * f) / det

  //Apply the inverse matrix to mouseX and mouseY
  const adjustedX = ia * x + ic * y + ie
  const adjustedY = ib * x + id * y + if_

  return { x: adjustedX, y: adjustedY }
}

/**
 * Given a point and a line segments start and end vertex, calculate the shortest distance from the provided point to any point that lands on the segment.
 * @param {number} px The z position of the point distance to be caculated
 * @param {number} py The y position of the point distance to be caculated
 * @param {number} ax The segments start vertex x position
 * @param {number} ay The segments start vertex y position
 * @param {number} bx The segments end vertex x position
 * @param {number} by The segments end vertex y position
 * @returns {number} Value representing the shortest distance from the provided point to any point that lands on the line segment.
 */
function pointToSegmentDistance(px: number, py: number, ax: number, ay: number, bx: number, by: number) {
  const A = px - ax
  const B = py - ay
  const C = bx - ax
  const D = by - ay

  const dot = A * C + B * D
  const len_sq = C * C + D * D
  const param = len_sq !== 0 ? dot / len_sq : -1

  let xx, yy

  if (param < 0) {
    xx = ax
    yy = ay
  } else if (param > 1) {
    xx = bx
    yy = by
  } else {
    xx = ax + param * C
    yy = ay + param * D
  }

  const dx = px - xx
  const dy = py - yy
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Given a line segment definition and dash properties, draws a dashed line representing the provided segment.
 * @param gfx Graphic class intance (i.e. p5)
 * @param {Segment} segment Line segment defined by a start point and an end point for each of the vertices
 * @param {number} dashLength Length of each dash
 * @param spaceLength Space between each dash line in the segment
 */
function drawDashedSegment(gfx: p5, segment: Segment, dashLength: number, spaceLength: number) {
  const dist = gfx.dist
  const lerp = gfx.lerp
  const start = segment.start
  const end = segment.end

  const distance = dist(start.x, start.y, end.x, end.y)
  const normalizedDashLength = dashLength / distance
  const normalizedSpaceLength = spaceLength / distance

  let t = 0
  while (t < 1) {
    const startT = t
    let endDashT = startT + normalizedDashLength
    endDashT = endDashT > 1 ? 1 : endDashT // Clamp to 1 to not overshoot the end
    const endX = lerp(start.x, end.x, endDashT)
    const endY = lerp(start.y, end.y, endDashT)
    gfx.line(lerp(start.x, end.x, startT), lerp(start.y, end.y, startT), endX, endY)

    t = endDashT + normalizedSpaceLength
  }
}

/**
 * Draw the defined segment
 * @param gfx Graphics class instance (i.e p5)
 * @param {Segment} segment Line segment defined by a start point and an end point for each of the vertices
 */
function drawSegment(gfx: p5, segment: Segment) {
  gfx.line(segment.start.x, segment.start.y, segment.end.x, segment.end.y)
}

export {
  orientation,
  getPolygonVertices,
  getRectangleVertices,
  doSegmentsIntersect,
  doesSegmentIntersectSegments,
  doRectanglesIntersect,
  doesRectangleIntersectPolygon,
  getIntersectingMarkers,
  calculateMidpoint,
  arePointsWithinDistance,
  polygonContainsPoint,
  withinSegment,
  isCollinear,
  clientToLocalCoordinates,
  applyMatrix,
  inverseApplyMatrix,
  pointToSegmentDistance,
  drawSegment,
  drawDashedSegment,
}
